import { getSupabaseClient, normalizeSupabaseError } from "./supabaseClient.js";

const TABLES = {
  FIXED_EXPENSES: "fixed_expenses",
  EVENT_EXPENSES: "event_expenses",
  EXPENSES_HISTORIC: "expenses_historic", // por si acaso
  VIEW_FORECAST: "v_expense_avg_by_region"
};

// =========================
// Helpers
// =========================
function throwSupabase(error, context = "") {
  if (!error) return;
  const n = normalizeSupabaseError(error);
  const msg = context ? `${context}: ${n.message}` : n.message;
  console.error(msg, n);
  throw new Error(msg);
}

export function sumExpenses(expenses = []) {
  if (!Array.isArray(expenses)) return 0;
  return expenses.reduce((acc, curr) => {
    const val = Number(curr.amount);
    return acc + (Number.isFinite(val) ? val : 0);
  }, 0);
}

/**
 * Desglose de gastos por owner (empresa / personal).
 * @returns {{ empresa: number, personal: number, reimbursable: number }}
 */
export function sumExpensesByOwner(expenses = []) {
  if (!Array.isArray(expenses)) return { empresa: 0, personal: 0, reimbursable: 0 };
  return expenses.reduce((acc, curr) => {
    const val = Number(curr.amount);
    const amt = Number.isFinite(val) ? val : 0;
    if (curr.expense_owner === "personal") {
      acc.personal += amt;
      if (curr.is_reimbursable) acc.reimbursable += amt;
    } else {
      acc.empresa += amt;
    }
    return acc;
  }, { empresa: 0, personal: 0, reimbursable: 0 });
}

// =========================
// Fixed Expenses
// =========================
export async function listFixedExpenses(teamId, { orderBy = "created_at", ascending = false, select = "*" } = {}) {
  if (!teamId) throw new Error("teamId es obligatorio para listFixedExpenses");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.FIXED_EXPENSES)
    .select(select)
    .eq("team_id", teamId)
    .order(orderBy, { ascending });

  throwSupabase(error, "Error listando gastos fijos");
  return data || [];
}

// =========================
// Event Expenses
// =========================

/**
 * Lista gastos de evento filtrando por team_id.
 */
export async function listEventExpensesByTeam(teamId, { orderBy = "expense_date", ascending = false, select = "*", limit = null } = {}) {
  if (!teamId) throw new Error("teamId es obligatorio para listEventExpensesByTeam");
  const supabase = getSupabaseClient();

  let q = supabase
    .from(TABLES.EVENT_EXPENSES)
    .select(`
      *,
      events:event_id(client_name_legacy, date, region, stage),
      venues:venue_id(name),
      contacts:contact_id(name, is_supplier, supplier_type, supplier_types)
    `)
    .eq("team_id", teamId)
    .order(orderBy, { ascending });

  if (limit) q = q.limit(limit);

  const { data, error } = await q;
  throwSupabase(error, "Error listando gastos de eventos");
  return data || [];
}

export async function listHistoricExpensesByRegionCategory(region, category, { limit = 500 } = {}) {
  if (!region || !category) return [];
  const supabase = getSupabaseClient();

  let q = supabase
    .from(TABLES.EXPENSES_HISTORIC)
    .select("legacy_event_id, expense_date, amount, expense_category, expense_subcategory, city, region, macro_region, id_expense")
    .eq("region", region)
    .eq("expense_category", category)
    .order("expense_date", { ascending: false });

  if (limit) q = q.limit(limit);

  const { data, error } = await q;
  throwSupabase(error, "Error listando gastos historicos");
  return data || [];
}

/**
 * Lista gastos para un conjunto de IDs de eventos.
 */
export async function listExpensesForEvents(eventIds = []) {
  if (!eventIds.length) return [];
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLES.EVENT_EXPENSES)
    .select("*")
    .in("event_id", eventIds);

  throwSupabase(error, "Error listando gastos por eventos");
  return data || [];
}

// =========================
// Forecast / Historic
// =========================

export const VIEW_EXPENSE_AVG = TABLES.VIEW_FORECAST;

/**
 * Obtiene la estimación de gastos para una región desde la vista SQL.
 * Retorna array de filas (categoría, medias).
 */
export async function getExpensesForecastByRegion(region) {
  if (!region) return [];

  const supabase = getSupabaseClient();

  // Asumimos que la vista tiene columnas como: region, expense_category, avg_amount...
  const { data, error } = await supabase
    .from("v_expense_benchmarks_by_region")
    .select("*")
    .eq("region", region)
    .maybeSingle();

  if (error) {
    console.warn("Error fetching expenses forecast:", error);
    return null;
  }

  return data; // Retorna objeto { region, forecast_total, forecast_by_category... } o null
}

/**
 * Obtiene la estimación de gastos con fallback por tramo de distancia.
 * 1. Intenta obtener datos directos de la región
 * 2. Si no hay histórico, calcula la media ponderada de todas las regiones del mismo tramo
 * 
 * @param {string} region - Región normalizada
 * @returns {object|null} - { forecast_total, forecast_by_category, sample_size, method }
 */
export async function getExpensesForecastByTier(region) {
  if (!region) return null;

  // 1. Intentar datos directos de la región
  const direct = await getExpensesForecastByRegion(region);
  if (direct && direct.forecast_total) {
    return { ...direct, method: "region_direct" };
  }

  // 2. Fallback: media del tramo
  const { getRegionsInSameTier } = await import("../constants/regionTiers.js");
  const tierRegions = getRegionsInSameTier(region);
  if (!tierRegions.length) return null;

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("v_expense_benchmarks_by_region")
    .select("*")
    .in("region", tierRegions);

  if (error) {
    console.warn("Error fetching tier expenses forecast:", error);
    return null;
  }

  if (!data || data.length === 0) return null;

  // Media ponderada por sample_size
  let totalWeight = 0;
  let weightedTotal = 0;
  const categoryAccum = {};

  for (const row of data) {
    const weight = row.sample_size || 1;
    totalWeight += weight;
    weightedTotal += (Number(row.forecast_total) || 0) * weight;

    const cats = row.forecast_by_category || {};
    for (const [cat, val] of Object.entries(cats)) {
      if (!categoryAccum[cat]) categoryAccum[cat] = { sum: 0, weight: 0 };
      categoryAccum[cat].sum += (Number(val) || 0) * weight;
      categoryAccum[cat].weight += weight;
    }
  }

  const avgTotal = totalWeight > 0 ? Math.round((weightedTotal / totalWeight) * 100) / 100 : 0;
  const avgCategories = {};
  for (const [cat, acc] of Object.entries(categoryAccum)) {
    avgCategories[cat] = acc.weight > 0 ? Math.round((acc.sum / acc.weight) * 100) / 100 : 0;
  }

  return {
    region,
    forecast_total: avgTotal,
    forecast_by_category: avgCategories,
    sample_size: totalWeight,
    method: "tier_avg",
  };
}

// =========================
// Event Expenses CRUD
// =========================

/**
 * Sube un ticket al bucket 'expense-receipts'.
 */
export async function uploadEventReceipt(file) {
  if (!file) return null;
  const supabase = getSupabaseClient();
  const safeName = (file.name || "ticket").replace(/[^a-zA-Z0-9.\-_]/g, "");
  const fileName = `receipts/${Date.now()}_${safeName || "ticket"}`;
  const { data, error } = await supabase.storage
    .from('expense-receipts')
    .upload(fileName, file, {
      contentType: file.type || "application/octet-stream",
      upsert: false,
    });

  if (error) {
    console.error("Error uploading receipt:", error);
    throw error;
  }
  return data.path; 
}

/**
 * Genera una Signed URL para ver un ticket privado.
 */
export async function getReceiptSignedUrl(path) {
  if (!path) return null;
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.storage
    .from('expense-receipts')
    .createSignedUrl(path, 3600); // 1 hour

  if (error) {
    console.error("Error generating signed URL:", error);
    return null;
  }
  return data.signedUrl;
}

export async function createEventExpense(payload) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(TABLES.EVENT_EXPENSES)
    .insert(payload)
    .select()
    .single();
  throwSupabase(error, "Error creando gasto");
  return data;
}

export async function updateEventExpenseReceipt(id, receiptPath) {
  if (!id) throw new Error("id es obligatorio para updateEventExpenseReceipt");
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(TABLES.EVENT_EXPENSES)
    .update({ receipt_url: receiptPath || null })
    .eq("id", id)
    .select()
    .single();
  throwSupabase(error, "Error actualizando el ticket del gasto");
  return data;
}

export async function deleteEventExpense(id) {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from(TABLES.EVENT_EXPENSES)
    .delete()
    .eq("id", id);
  throwSupabase(error, "Error eliminando gasto");
  return true;
}

// =========================
// Fixed Expenses CRUD
// =========================

export async function createFixedExpense(payload) {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(TABLES.FIXED_EXPENSES)
    .insert(payload)
    .select()
    .single();
  throwSupabase(error, "Error creando gasto fijo");
  return data;
}

export async function deleteFixedExpense(id) {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from(TABLES.FIXED_EXPENSES)
    .delete()
    .eq("id", id);
  throwSupabase(error, "Error eliminando gasto fijo");
  return true;
}
